// ============================================================
// Motor de cálculo de riesgo ergonómico — Método RULA
// (Rapid Upper Limb Assessment — McAtamney & Corlett, 1993)
// Fuente: McAtamney, L. & Corlett, E.N. (1993). RULA: a survey method for
// the investigation of work-related upper limb disorders. Applied
// Ergonomics, 24(2), 91-99.
//
// Alcance de esta primera fase (motor de cálculo puro, sin UI todavía):
//   - Puntuación de brazo, antebrazo, muñeca y giro de muñeca (Grupo A,
//     miembro superior) — Tablas 1-7
//   - Puntuación de cuello, tronco y piernas (Grupo B) — Tablas 8-12
//   - Puntuación por tipo de actividad y por carga/fuerza — Tablas 15-16
//   - Puntuación del Grupo A (Tabla 13) y Grupo B (Tabla 14)
//   - Puntuación C y D (Grupo A/B + actividad + carga)
//   - Puntuación final RULA (Tabla 17) y nivel de actuación (Tabla 18)
//
// Las Tablas 13, 14 y 17 (doble entrada) fueron extraídas de la fuente vía
// fetch automático y confirmadas celda por celda contra la fuente original
// por el usuario (2026) antes de implementarlas — no modificar esas
// tablas sin repetir esa verificación manual.
// ============================================================

function clamp(valor: number, min: number, max: number): number {
  return Math.min(Math.max(valor, min), max);
}

// ===== 1. Puntuación del brazo (Tabla 1 + modificadores Tabla 2) =====

export type RangoBrazo =
  | 'neutro'                    // 20° extensión a 20° flexión → 1
  | 'extension20_o_flexion20a45' // extensión >20° o flexión >20° y <45° → 2
  | 'flexion45a90'               // flexión >45° y 90° → 3
  | 'flexionMayor90';            // flexión >90° → 4

export interface ModificadoresBrazo {
  hombroElevadoORotado: boolean; // +1 (Tabla 2)
  abducido: boolean;              // +1 (Tabla 2)
  puntoApoyo: boolean;            // -1 (Tabla 2)
}

// Tabla 1 RULA: puntuación base del brazo según rango de flexión/extensión.
const PUNTUACION_BASE_BRAZO: Record<RangoBrazo, number> = {
  neutro: 1,
  extension20_o_flexion20a45: 2,
  flexion45a90: 3,
  flexionMayor90: 4,
};

// Tabla 2 RULA: los 3 modificadores del brazo NO son excluyentes entre sí,
// se pueden combinar y sumar/restar todos los que apliquen.
//
// La puntuación del brazo se acota a 1-6 (las filas "Brazo" de la Tabla 13).
// Brazo neutro (1) + punto de apoyo (−1) daría 0, que no existe en la
// Tabla 13: se lleva a 1. SUPUESTO pendiente de validar (2026): la fuente
// no define un brazo 0; se toma el mínimo de la escala.
export function calcularPuntuacionBrazo(
  rango: RangoBrazo,
  modificadores: ModificadoresBrazo
): number {
  let puntuacion = PUNTUACION_BASE_BRAZO[rango];
  if (modificadores.hombroElevadoORotado) puntuacion += 1;
  if (modificadores.abducido) puntuacion += 1;
  if (modificadores.puntoApoyo) puntuacion -= 1;
  return clamp(puntuacion, 1, 6);
}

// ===== 2. Puntuación del antebrazo (Tabla 3 + modificador Tabla 4) =====

export type RangoAntebrazo =
  | 'flexion60a100'    // flexión entre 60° y 100° → 1
  | 'fueraDe60a100';   // flexión <60° o >100° → 2

export type ModificadorAntebrazo = 'ninguno' | 'aLadoDelCuerpo' | 'cruzaLineaMedia';

const PUNTUACION_BASE_ANTEBRAZO: Record<RangoAntebrazo, number> = {
  flexion60a100: 1,
  fueraDe60a100: 2,
};

// Tabla 4 RULA: "a un lado del cuerpo" y "cruza la línea media" son
// EXCLUYENTES entre sí (máximo +1 total, no se suman ambos a la vez).
export function calcularPuntuacionAntebrazo(
  rango: RangoAntebrazo,
  modificador: ModificadorAntebrazo
): number {
  const base = PUNTUACION_BASE_ANTEBRAZO[rango];
  return modificador === 'ninguno' ? base : base + 1;
}

// ===== 3. Puntuación de la muñeca (Tabla 5 + modificador Tabla 6) =====

export type RangoMuneca =
  | 'neutra'                        // posición neutra → 1
  | 'flexionExtension0a15'          // flexión o extensión >0° y <15° → 2
  | 'flexionExtensionMayor15';      // flexión o extensión >15° → 3

export type ModificadorMuneca = 'ninguno' | 'desviacionRadial' | 'desviacionCubital';

const PUNTUACION_BASE_MUNECA: Record<RangoMuneca, number> = {
  neutra: 1,
  flexionExtension0a15: 2,
  flexionExtensionMayor15: 3,
};

// Tabla 6 RULA: desviación radial y cubital son EXCLUYENTES entre sí
// (máximo +1 total, no se suman ambas a la vez).
export function calcularPuntuacionMuneca(
  rango: RangoMuneca,
  modificador: ModificadorMuneca
): number {
  const base = PUNTUACION_BASE_MUNECA[rango];
  return modificador === 'ninguno' ? base : base + 1;
}

// ===== 4. Puntuación del giro de muñeca (Tabla 7, independiente) =====

export type RangoGiroMuneca =
  | 'medio'    // pronación o supinación media → 1
  | 'extremo'; // pronación o supinación extrema → 2

const PUNTUACION_GIRO_MUNECA: Record<RangoGiroMuneca, number> = {
  medio: 1,
  extremo: 2,
};

// Tabla 7 RULA: puntuación del giro de muñeca, NO se suma a la puntuación
// de la muñeca (Tabla 5/6) — es un insumo independiente de la Tabla 13.
export function calcularPuntuacionGiroMuneca(rango: RangoGiroMuneca): number {
  return PUNTUACION_GIRO_MUNECA[rango];
}

// ===== 5. Puntuación del Grupo A (Tabla 13) =====

// Tabla 13 RULA: tabla de doble entrada Brazo (1-6) x Antebrazo (1-3) por
// fila, Muñeca (1-4) x Giro de muñeca (1-2) por columna (8 columnas:
// M1G1, M1G2, M2G1, M2G2, M3G1, M3G2, M4G1, M4G2). Extraída de
// McAtamney, L. & Corlett, E.N. (1993). RULA: a survey method for the
// investigation of work-related upper limb disorders. Applied Ergonomics,
// 24(2), 91-99, y confirmada celda por celda contra la fuente por el
// usuario (2026) — no modificar sin repetir esa verificación.
const TABLA_13_GRUPO_A: number[][] = [
  // Brazo 1
  [1, 2, 2, 2, 2, 3, 3, 3], // Antebrazo 1
  [2, 2, 2, 2, 3, 3, 3, 3], // Antebrazo 2
  [2, 3, 3, 3, 3, 3, 4, 4], // Antebrazo 3
  // Brazo 2
  [2, 3, 3, 3, 3, 4, 4, 4],
  [3, 3, 3, 3, 3, 4, 4, 4],
  [3, 4, 4, 4, 4, 4, 5, 5],
  // Brazo 3
  [3, 3, 4, 4, 4, 4, 5, 5],
  [3, 4, 4, 4, 4, 4, 5, 5],
  [4, 4, 4, 4, 4, 5, 5, 5],
  // Brazo 4
  [4, 4, 4, 4, 4, 5, 5, 5],
  [4, 4, 4, 4, 4, 5, 5, 5],
  [4, 4, 4, 5, 5, 5, 6, 6],
  // Brazo 5
  [5, 5, 5, 5, 5, 6, 6, 7],
  [5, 6, 6, 6, 6, 7, 7, 7],
  [6, 6, 6, 7, 7, 7, 7, 8],
  // Brazo 6
  [7, 7, 7, 7, 7, 8, 8, 9],
  [8, 8, 8, 8, 8, 9, 9, 9],
  [9, 9, 9, 9, 9, 9, 9, 9],
];

// Entradas acotadas al rango de la Tabla 13 (brazo 1-6, antebrazo 1-3,
// muñeca 1-4, giro 1-2), igual que reba.ts: un valor fuera de rango ya no
// indexa fuera de la tabla.
export function calcularPuntuacionGrupoA(
  brazo: number,
  antebrazo: number,
  muneca: number,
  giroMuneca: number
): number {
  const b = clamp(brazo, 1, 6);
  const a = clamp(antebrazo, 1, 3);
  const m = clamp(muneca, 1, 4);
  const g = clamp(giroMuneca, 1, 2);
  const fila = (b - 1) * 3 + (a - 1);
  const columna = (m - 1) * 2 + (g - 1);
  return TABLA_13_GRUPO_A[fila][columna];
}

// ===== 6. Puntuación del cuello (Tabla 8 + modificadores Tabla 9) =====

export type RangoCuello =
  | 'flexion0a10'       // flexión entre 0° y 10° → 1
  | 'flexion10a20'      // flexión >10° y ≤20° → 2
  | 'flexionMayor20'    // flexión >20° → 3
  | 'extension';         // extensión en cualquier grado → 4

export interface ModificadoresCuello {
  rotada: boolean;              // +1 (Tabla 9)
  inclinacionLateral: boolean;  // +1 (Tabla 9)
}

const PUNTUACION_BASE_CUELLO: Record<RangoCuello, number> = {
  flexion0a10: 1,
  flexion10a20: 2,
  flexionMayor20: 3,
  extension: 4,
};

// Tabla 9 RULA: rotación e inclinación lateral del cuello SÍ pueden
// combinarse (hasta +2 total).
export function calcularPuntuacionCuello(
  rango: RangoCuello,
  modificadores: ModificadoresCuello
): number {
  let puntuacion = PUNTUACION_BASE_CUELLO[rango];
  if (modificadores.rotada) puntuacion += 1;
  if (modificadores.inclinacionLateral) puntuacion += 1;
  return puntuacion;
}

// ===== 7. Puntuación del tronco (Tabla 10 + modificadores Tabla 11) =====

export type RangoTronco =
  | 'erguidoOApoyado'    // de pie erguido sin flex/ext, o sentado bien
                          // apoyado con ángulo tronco-piernas >90° → 1
  | 'flexion0a20'         // flexión >0° y ≤20° → 2
  | 'flexion20a60'        // flexión >20° y ≤60° → 3
  | 'flexionMayor60';     // flexión >60° → 4

export interface ModificadoresTronco {
  rotado: boolean;              // +1 (Tabla 11)
  inclinacionLateral: boolean;  // +1 (Tabla 11)
}

const PUNTUACION_BASE_TRONCO: Record<RangoTronco, number> = {
  erguidoOApoyado: 1,
  flexion0a20: 2,
  flexion20a60: 3,
  flexionMayor60: 4,
};

// Tabla 11 RULA: rotación e inclinación lateral del tronco SÍ pueden
// combinarse (hasta +2 total).
export function calcularPuntuacionTronco(
  rango: RangoTronco,
  modificadores: ModificadoresTronco
): number {
  let puntuacion = PUNTUACION_BASE_TRONCO[rango];
  if (modificadores.rotado) puntuacion += 1;
  if (modificadores.inclinacionLateral) puntuacion += 1;
  return puntuacion;
}

// ===== 8. Puntuación de las piernas (Tabla 12) =====

export type CondicionPiernas =
  | 'apoyadasYEquilibrado'    // sentado con piernas y pies bien apoyados,
                                // o de pie con peso simétrico y espacio
                                // para cambiar de posición → 1
  | 'noApoyadasOAsimetrico';   // pies no apoyados o peso no simétrico → 2

const PUNTUACION_PIERNAS: Record<CondicionPiernas, number> = {
  apoyadasYEquilibrado: 1,
  noApoyadasOAsimetrico: 2,
};

export function calcularPuntuacionPiernas(condicion: CondicionPiernas): number {
  return PUNTUACION_PIERNAS[condicion];
}

// ===== 9. Puntuación del Grupo B (Tabla 14) =====

// Tabla 14 RULA: tabla de doble entrada Cuello (1-6) por fila, Tronco
// (1-6) x Piernas (1-2) por columna (12 columnas: T1P1, T1P2, ..., T6P1,
// T6P2). Extraída de McAtamney, L. & Corlett, E.N. (1993). RULA: a survey
// method for the investigation of work-related upper limb disorders.
// Applied Ergonomics, 24(2), 91-99, y confirmada celda por celda contra la
// fuente por el usuario (2026) — no modificar sin repetir esa verificación.
const TABLA_14_GRUPO_B: number[][] = [
  [1, 3, 2, 3, 3, 4, 5, 5, 6, 6, 7, 7], // Cuello 1
  [2, 3, 2, 3, 4, 5, 5, 5, 6, 7, 7, 7], // Cuello 2
  [3, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 7], // Cuello 3
  [5, 5, 5, 6, 6, 7, 7, 7, 7, 7, 8, 8], // Cuello 4
  [7, 7, 7, 7, 7, 8, 8, 8, 8, 8, 8, 8], // Cuello 5
  [8, 8, 8, 8, 8, 8, 8, 9, 9, 9, 9, 9], // Cuello 6
];

// Entradas acotadas al rango de la Tabla 14 (cuello 1-6, tronco 1-6,
// piernas 1-2). Con los insumos del formulario ya no se salen (máximo 4+2),
// se acota por defensa, igual que el Grupo A.
export function calcularPuntuacionGrupoB(
  cuello: number,
  tronco: number,
  piernas: number
): number {
  const c = clamp(cuello, 1, 6);
  const t = clamp(tronco, 1, 6);
  const p = clamp(piernas, 1, 2);
  const fila = c - 1;
  const columna = (t - 1) * 2 + (p - 1);
  return TABLA_14_GRUPO_B[fila][columna];
}

// ===== 10. Puntuación por tipo de actividad (Tabla 15) =====

export interface TipoActividad {
  estatica: boolean;    // +1 si la postura se mantiene >1 minuto seguido
  repetitiva: boolean;  // +1 si se repite >4 veces/minuto
  // 'ocasional' es la ausencia de ambas (0) — no requiere campo propio.
}

// Tabla 15 RULA: estática y repetitiva pueden sumarse ambas si aplican
// simultáneamente (no son excluyentes).
function calcularPuntuacionActividad(actividad: TipoActividad): number {
  let puntuacion = 0;
  if (actividad.estatica) puntuacion += 1;
  if (actividad.repetitiva) puntuacion += 1;
  return puntuacion;
}

// ===== 11. Puntuación por carga/fuerza (Tabla 16) =====

export type CargaFuerza =
  | 'menor2kgIntermitente'            // carga <2kg intermitente → 0
  | 'de2a10kgIntermitente'            // carga 2-10kg intermitente → +1
  | 'de2a10kgEstaticaORepetitiva'     // carga 2-10kg estática o repetitiva → +2
  | 'mayor10kgIntermitente'           // carga >10kg intermitente → +2
  | 'mayor10kgEstaticaORepetitiva'    // carga >10kg estática o repetitiva → +3
  | 'golpesOFuerzasBruscas';          // golpes o fuerzas bruscas → +3

const PUNTUACION_CARGA_FUERZA: Record<CargaFuerza, number> = {
  menor2kgIntermitente: 0,
  de2a10kgIntermitente: 1,
  de2a10kgEstaticaORepetitiva: 2,
  mayor10kgIntermitente: 2,
  mayor10kgEstaticaORepetitiva: 3,
  golpesOFuerzasBruscas: 3,
};

// ===== 12. Puntuación C y D =====

// RULA: Puntuación C = Puntuación Grupo A + puntuación de actividad +
// puntuación de carga/fuerza (mismos insumos de actividad/carga que D).
export function calcularPuntuacionC(
  grupoA: number,
  tipoActividad: TipoActividad,
  cargaFuerza: CargaFuerza
): number {
  return grupoA + calcularPuntuacionActividad(tipoActividad) + PUNTUACION_CARGA_FUERZA[cargaFuerza];
}

// RULA: Puntuación D = Puntuación Grupo B + puntuación de actividad +
// puntuación de carga/fuerza.
export function calcularPuntuacionD(
  grupoB: number,
  tipoActividad: TipoActividad,
  cargaFuerza: CargaFuerza
): number {
  return grupoB + calcularPuntuacionActividad(tipoActividad) + PUNTUACION_CARGA_FUERZA[cargaFuerza];
}

// ===== 13. Puntuación final RULA (Tabla 17) =====

// Tabla 17 RULA: tabla de doble entrada Puntuación C (filas 1-8) x
// Puntuación D (columnas 1-7). Extraída de McAtamney, L. & Corlett, E.N.
// (1993). RULA: a survey method for the investigation of work-related
// upper limb disorders. Applied Ergonomics, 24(2), 91-99, y confirmada
// celda por celda contra la fuente por el usuario (2026) — no modificar
// sin repetir esa verificación.
const TABLA_17_PUNTUACION_FINAL: number[][] = [
  [1, 2, 3, 3, 4, 5, 5], // C=1
  [2, 2, 3, 4, 4, 5, 5], // C=2
  [3, 3, 3, 4, 4, 5, 6], // C=3
  [3, 3, 3, 4, 5, 6, 6], // C=4
  [4, 4, 4, 5, 6, 7, 7], // C=5
  [4, 4, 5, 6, 6, 7, 7], // C=6
  [5, 5, 6, 6, 7, 7, 7], // C=7
  [5, 5, 6, 7, 7, 7, 7], // C=8
];

// Tabla 17 RULA: la fila 8 vale para C = "8+" y la columna 7 para D = "7+"
// (McAtamney y Corlett, 1993). C sí puede pasar de 8: Grupo A llega a 9 y
// calcularPuntuacionC le suma hasta +2 (actividad) y +3 (carga) → C ≤ 14.
// Por eso C se acota a 1-8 y D a 1-7.
export function calcularPuntuacionFinalRula(
  puntuacionC: number,
  puntuacionD: number
): number {
  const fila = clamp(puntuacionC, 1, 8) - 1;
  const columna = clamp(puntuacionD, 1, 7) - 1;
  return TABLA_17_PUNTUACION_FINAL[fila][columna];
}

// ===== 14. Nivel de actuación (Tabla 18) =====

export interface NivelActuacionRula {
  nivel: number;
  descripcion: string;
}

// Tabla 18 RULA: niveles de actuación según la puntuación final (1-7).
export function obtenerNivelActuacion(puntuacionFinal: number): NivelActuacionRula {
  if (puntuacionFinal <= 2) {
    return { nivel: 1, descripcion: 'Riesgo Aceptable' };
  }
  if (puntuacionFinal <= 4) {
    return { nivel: 2, descripcion: 'Pueden requerirse cambios, profundizar estudio' };
  }
  if (puntuacionFinal <= 6) {
    return { nivel: 3, descripcion: 'Se requiere rediseño de la tarea' };
  }
  return { nivel: 4, descripcion: 'Cambios urgentes' };
}

// ============================================================
// Verificación manual — postura neutra completa (caso de referencia)
// ============================================================
// const brazo = calcularPuntuacionBrazo('neutro', {
//   hombroElevadoORotado: false, abducido: false, puntoApoyo: false,
// }); // → 1 (Tabla 1, sin modificadores)
//
// const antebrazo = calcularPuntuacionAntebrazo('flexion60a100', 'ninguno'); // → 1
// const muneca = calcularPuntuacionMuneca('neutra', 'ninguno');              // → 1
// const giroMuneca = calcularPuntuacionGiroMuneca('medio');                  // → 1
//
// const grupoA = calcularPuntuacionGrupoA(brazo, antebrazo, muneca, giroMuneca);
// // fila = (1-1)*3 + (1-1) = 0, columna = (1-1)*2 + (1-1) = 0
// // TABLA_13_GRUPO_A[0][0] = 1 → grupoA = 1
//
// const cuello = calcularPuntuacionCuello('flexion0a10', {
//   rotada: false, inclinacionLateral: false,
// }); // → 1
//
// const tronco = calcularPuntuacionTronco('erguidoOApoyado', {
//   rotado: false, inclinacionLateral: false,
// }); // → 1
//
// const piernas = calcularPuntuacionPiernas('apoyadasYEquilibrado'); // → 1
//
// const grupoB = calcularPuntuacionGrupoB(cuello, tronco, piernas);
// // fila = 1-1 = 0, columna = (1-1)*2 + (1-1) = 0
// // TABLA_14_GRUPO_B[0][0] = 1 → grupoB = 1
//
// const sinActividad: TipoActividad = { estatica: false, repetitiva: false };
// const sinCarga: CargaFuerza = 'menor2kgIntermitente';
//
// const c = calcularPuntuacionC(grupoA, sinActividad, sinCarga); // → 1 + 0 + 0 = 1
// const d = calcularPuntuacionD(grupoB, sinActividad, sinCarga); // → 1 + 0 + 0 = 1
//
// const puntuacionFinal = calcularPuntuacionFinalRula(c, d);
// // fila = 1-1 = 0, columna = min(1,7)-1 = 0 → TABLA_17_PUNTUACION_FINAL[0][0] = 1
//
// const nivel = obtenerNivelActuacion(puntuacionFinal);
// // puntuacionFinal=1 ≤ 2 → { nivel: 1, descripcion: 'Riesgo Aceptable' }
//
// Resultado esperado (confirmado a mano antes de guardar este archivo):
//   brazo=1, antebrazo=1, muñeca=1, giroMuñeca=1 → grupoA=1
//   cuello=1, tronco=1, piernas=1 → grupoB=1
//   sin actividad ni carga → C=1, D=1 → puntuaciónFinal=1 → Nivel 1,
//   "Riesgo Aceptable" — coincide exactamente con el caso de verificación
//   planteado por el usuario.
//
// Ejemplo de calcularPuntuacionC / calcularPuntuacionD con actividad y
// carga (no depende de las tablas pendientes, usa un grupoA/grupoB de
// ejemplo = 3):
//
// const c = calcularPuntuacionC(3, { estatica: true, repetitiva: false }, 'de2a10kgIntermitente');
// // c = 3 (grupoA) + 1 (estática) + 1 (carga 2-10kg intermitente) = 5
//
// const d = calcularPuntuacionD(3, { estatica: false, repetitiva: true }, 'mayor10kgEstaticaORepetitiva');
// // d = 3 (grupoB) + 1 (repetitiva) + 3 (carga >10kg estática/repetitiva) = 7
//
// Resultado esperado (confirmado a mano): c=5, d=7 — suma aritmética
// simple de los 3 componentes, sin tabla de doble entrada involucrada.
// ============================================================
